import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Button } from "../components/ui/button";
import axios from "axios";
import { GridStack, GridStackWidget } from "gridstack";
import "gridstack/dist/gridstack.min.css";
import Tile, { TileType } from "../types";
import ImageTile from "../components/tiles/ImageTile";
import HeaderTile from "../components/tiles/HeaderTile";
import TypeTile from "../components/tiles/TypeTile";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import Menu from "./Menu";

const availableTileTypes = [
    { id: "new-image", title: "Image Tile", type: "image" },
    { id: "new-header", title: "Header Tile", type: "header" },
    { id: "new-type", title: "Data Tile", type: "type" },
];

const Dashboard = () => {
    const { user, loading } = useAuth();
    const [tiles, setTiles] = useState<Tile[]>([]);
    const [tilesReady, setTilesReady] = useState(false);
    const [setupMode, setSetupMode] = useState(false);
    const navigate = useNavigate();

    const gridRef = useRef<HTMLDivElement>(null);
    const gridInstance = useRef<GridStack | null>(null);
    const containerMap = useRef<Record<string, HTMLDivElement>>({});
    const mountedTiles = useRef<Set<string>>(new Set());
    const tilesRef = useRef<Tile[]>([]);

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalHeaderText, setModalHeaderText] = useState("");
    const [modalTileId, setModalTileId] = useState<string | null>(null);

    const userId = user?.uid ?? "";

    useEffect(() => {
        if (!loading && !userId) {
            navigate("/login", { replace: true });
        }
    }, [loading, userId, navigate]);

    useEffect(() => {
        tilesRef.current = tiles;
    }, [tiles]);

    useEffect(() => {
        const loadData = async () => {
            if (!userId) return;
            try {
                const res = await axios.get(api.getDashboardLayout(userId));
                const tileData = res.data?.tiles;
                if (Array.isArray(tileData)) {
                    setTiles(tileData);
                    setSetupMode(false);
                } else {
                    setSetupMode(true);
                }
            } catch (error) {
                console.error("Failed to load layout:", error);
                setSetupMode(true);
            }
        };
        loadData();
    }, [userId]);

    useEffect(() => {
        const gridEl = gridRef.current;
        if (!gridEl || !setupMode) return;
        if (gridInstance.current) return;

        const grid = GridStack.init({ float: true, column: 12, cellHeight: 30 }, gridEl);
        gridInstance.current = grid;

        const onChange = (_: any, updated: GridStackWidget[]) => {
            const updatedTiles = updated
                .map((item) => {
                    const original = tilesRef.current.find((t) => t.id === item.id);
                    if (!original) return null;
                    return {
                        ...original,
                        config: {
                            ...original.config,
                            x: item.x ?? 0,
                            y: item.y ?? 0,
                            gridColumn: item.w ?? 3,
                            gridRow: item.h ?? 4,
                        },
                    };
                })
                .filter(Boolean) as Tile[];

            setTiles((prevTiles) =>
                prevTiles.map((tile) => {
                    const updated = updatedTiles.find((u) => u.id === tile.id);
                    return updated ? updated : tile;
                })
            );
        };

        grid.on("change", onChange);

        return () => {
            grid.off("change");
            if (gridEl?.parentNode) {
                grid.destroy(false);
            }
            gridInstance.current = null;
            mountedTiles.current.clear();
            containerMap.current = {};
        };
    }, [setupMode]);

    useEffect(() => {
        if (!setupMode) return;
        const grid = gridInstance.current;
        if (!grid) return;

        // Remove widgets for tiles no longer in state
        mountedTiles.current.forEach((id) => {
            if (!tiles.find((t) => t.id === id)) {
                const widgetEl = containerMap.current[id]?.parentElement;
                if (widgetEl && widgetEl.parentElement) {
                    grid.removeWidget(widgetEl, true);
                }
                mountedTiles.current.delete(id);
                delete containerMap.current[id];
            }
        });

        // Add new widgets for tiles not yet mounted
        tiles.forEach((tile) => {
            if (mountedTiles.current.has(tile.id)) return;

            const widget = document.createElement("div");
            widget.className = "grid-stack-item";

            // **Important: Use "gs-id" attribute (no "data-" prefix) for GridStack to track tile id properly**
            widget.setAttribute("gs-id", tile.id);
            widget.setAttribute("gs-x", `${tile.config?.x ?? 0}`);
            widget.setAttribute("gs-y", `${tile.config?.y ?? 0}`);
            widget.setAttribute("gs-w", `${tile.config?.gridColumn ?? 8}`);
            widget.setAttribute("gs-h", `${tile.config?.gridRow ?? 10}`);

            const content = document.createElement("div");
            content.className = "grid-stack-item-content p-2 bg-white rounded shadow overflow-auto relative";

            if (setupMode) {
                const closeButton = document.createElement("button");
                closeButton.innerText = "×";
                closeButton.className = "absolute top-0 right-1 text-xl text-red-600 hover:text-red-800 z-10";
                closeButton.onclick = () => {
                    setTiles((prev) => prev.filter((t) => t.id !== tile.id));
                    delete containerMap.current[tile.id];
                    mountedTiles.current.delete(tile.id);
                    grid.removeWidget(widget);
                };
                content.appendChild(closeButton);
            }

            widget.appendChild(content);
            grid.makeWidget(widget);

            containerMap.current[tile.id] = content;
            mountedTiles.current.add(tile.id);
        });

        // Trigger a React re-render to update portals once containers are mounted
        requestAnimationFrame(() => {
            setTilesReady((prev) => !prev); // force a minimal rerender
        });
    }, [tiles, setupMode]);

    useEffect(() => {
        if (setupMode) return; // don't run when editing

        const gridEl = gridRef.current;
        if (!gridEl) return;

        const grid = GridStack.init({
            float: true,
            column: 12,
            cellHeight: 30,
            staticGrid: true, // disables drag/resize
        }, gridEl);

        gridInstance.current = grid;

        // Add widgets for all tiles here similar to edit mode,
        // but no close buttons or interactive handlers.

        tiles.forEach(tile => {
            const widget = document.createElement("div");
            widget.className = "grid-stack-item";
            widget.setAttribute("gs-id", tile.id);
            widget.setAttribute("gs-x", `${tile.config?.x ?? 0}`);
            widget.setAttribute("gs-y", `${tile.config?.y ?? 0}`);
            widget.setAttribute("gs-w", `${tile.config?.gridColumn ?? 8}`);
            widget.setAttribute("gs-h", `${tile.config?.gridRow ?? 10}`);

            const content = document.createElement("div");
            content.className = "grid-stack-item-content p-2 bg-white rounded shadow overflow-auto relative";

            widget.appendChild(content);
            grid.makeWidget(widget);

            containerMap.current[tile.id] = content;
        });

        requestAnimationFrame(() => {
            setTilesReady((prev) => !prev); // force a minimal rerender
        });

        return () => {
            grid.destroy(false);
            gridInstance.current = null;
            containerMap.current = {};
        };
    }, [setupMode, tiles]);

    const openEditModal = (tile: Tile) => {
        setModalTileId(tile.id);
        setModalHeaderText(tile.config?.headerText || "");
        setIsModalOpen(true);
    };

    const saveModalChanges = () => {
        if (modalTileId) {
            updateTileConfig(modalTileId, { headerText: modalHeaderText });
        }
        setIsModalOpen(false);
    };

    const updateTileConfig = (id: string, config: Partial<Tile["config"]>) => {
        setTiles((prev) =>
            prev.map((tile) =>
                tile.id === id ? { ...tile, config: { ...tile.config, ...config } } : tile
            )
        );
    };

    const handleSave = async () => {
        try {
            if (gridInstance.current) {
                const layout = gridInstance.current.save(false) as GridStackWidget[];
                if (Array.isArray(layout)) {
                    // Map saved layout positions back to tiles using widget.id (which comes from gs-id)
                    const updatedTiles = tilesRef.current.map((tile) => {
                        const widget = layout.find((w) => w.id === tile.id);
                        if (!widget) return tile;
                        return {
                            ...tile,
                            config: {
                                ...tile.config,
                                x: widget.x ?? 0,
                                y: widget.y ?? 0,
                                gridColumn: widget.w ?? 8,
                                gridRow: widget.h ?? 10,
                            },
                        };
                    });
                    setTiles(updatedTiles);
                    tilesRef.current = updatedTiles;
                }
            }
            await axios.post(api.saveDashboardLayout(userId), { tiles: tilesRef.current });
            setSetupMode(false);
        } catch (err) {
            console.error("Failed to save layout", err);
        }
    };

    const addTile = (type: TileType, title: string) => {
        const newId = `${type}-${Date.now()}`;
        const maxY = tiles.reduce((max, tile) => {
            const y = tile.config?.y ?? 0;
            const h = tile.config?.gridRow ?? 4;
            return Math.max(max, y + h);
        }, 0);

        const newTile: Tile = {
            id: newId,
            title,
            type,
            items: [],
            config: { gridColumn: 8, gridRow: 10, x: 0, y: maxY },
        };

        setTiles((prev) => {
            const updated = [...prev, newTile];
            tilesRef.current = updated;
            return updated;
        });
    };

    const ComponentForTile = (tile: Tile) => {
        const props = { tile, updateTileConfig, setupMode };
        switch (tile.type) {
            case "header":
                return <HeaderTile {...props} onEditClick={() => openEditModal(tile)} />;
            case "type":
                return <TypeTile {...props} />;
            default:
                return <ImageTile {...props} />;
        }
    };

    const TilePortals = () => {
        return (
            <>
                {tiles.map((tile) => {
                    const container = containerMap.current[tile.id];
                    return container ? createPortal(ComponentForTile(tile), container) : null;
                })}
            </>
        );
    };

    if (loading) return <div>Loading user info...</div>;
    if (!userId) return null;

    return setupMode ? (
        <div>
            <div className="w-full p-4 bg-white border-b flex flex-wrap gap-4 items-center justify-between">
                <div className="flex flex-wrap gap-2">
                    {availableTileTypes.map((tile) => (
                        <div
                            key={tile.id}
                            onClick={() => addTile(tile.type as TileType, tile.title)}
                            className="p-2 bg-gray-200 rounded cursor-pointer hover:bg-gray-300"
                        >
                            {tile.title}
                        </div>
                    ))}
                </div>
                <Button onClick={handleSave}>Save Dashboard</Button>
            </div>
            <main className="flex-1 p-4 relative">
                <div ref={gridRef} className="grid-stack" style={{ minHeight: "400px" }} />
                <TilePortals />
            </main>
            {isModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-40 z-50 flex items-center justify-center">
                    <div className="bg-white p-6 rounded shadow-lg w-full max-w-md">
                        <h2 className="text-xl font-bold mb-4">Edit Header</h2>
                        <input
                            type="text"
                            className="w-full border px-3 py-2 rounded mb-4"
                            value={modalHeaderText}
                            onChange={(e) => setModalHeaderText(e.target.value)}
                        />
                        <div className="flex justify-end space-x-2">
                            <Button variant="ghost" onClick={() => setIsModalOpen(false)}>
                                Cancel
                            </Button>
                            <Button onClick={saveModalChanges}>Save</Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    ) : (
        <div className="min-h-screen bg-[#E4F1FF] text-[#333333] p-6">
            <header className="mb-8 flex justify-between items-center">
                <h1 className="text-4xl font-bold">Author Dashboard</h1>
                <div>
                    <Menu setSetupMode={() => setSetupMode(true)} />
                </div>
            </header>
            <div className="relative">
                <div ref={gridRef} className="grid-stack" style={{ minHeight: "400px" }} />
                <TilePortals />
            </div>
        </div>
    );
};

export default Dashboard;
